import { Controller } from "@hotwired/stimulus";

/**
 * Controller for managing email preferences toggles.
 * Handles subscription/unsubscription with optimistic UI and loading states.
 *
 * Usage:
 *   <div data-controller="email-preferences">
 *     <input
 *       type="checkbox"
 *       data-action="change->email-preferences#toggleSubscription"
 *       data-email-preferences-target="checkbox"
 *       data-url="/app/settings/email_preferences/subscription"
 *     />
 *   </div>
 */
export default class extends Controller {
  static targets = ["checkbox", "item"];
  static values = { csrfToken: String };

  declare readonly checkboxTargets: HTMLInputElement[];
  declare readonly itemTargets: HTMLElement[];
  declare readonly csrfTokenValue: string;

  /**
   * Toggle global marketing email subscription
   */
  async toggleSubscription(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const url = checkbox.dataset.url;

    if (!url) {
      console.error("email-preferences: No URL provided for subscription toggle");
      return;
    }

    await this.performToggle(checkbox, url, { subscribed: checkbox.checked });
  }

  /**
   * Toggle subscription to a specific mailing list
   */
  async toggleList(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const url = checkbox.dataset.url;
    const listId = checkbox.dataset.listId;

    if (!url || !listId) {
      console.error("email-preferences: No URL or list ID provided for list toggle");
      return;
    }

    await this.performToggle(checkbox, url, { subscribed: checkbox.checked });
  }

  /**
   * Perform the toggle request with loading state
   */
  private async performToggle(
    checkbox: HTMLInputElement,
    url: string,
    params: Record<string, boolean>
  ) {
    // Find parent item for visual feedback
    const item = checkbox.closest("[data-email-preferences-target='item']") as HTMLElement;

    // Disable all checkboxes during request
    this.setLoadingState(true, item);

    try {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, String(value));
      });

      const response = await fetch(`${url}?${searchParams.toString()}`, {
        method: "PATCH",
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html",
          "X-CSRF-Token": this.csrfToken,
        },
        credentials: "same-origin",
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type") || "";

        if (contentType.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text();
          Turbo.renderStreamMessage(html);
        }
      } else {
        // Revert checkbox on error
        checkbox.checked = !checkbox.checked;
        console.error("email-preferences: Request failed", response.status);
      }
    } catch (error) {
      // Revert checkbox on network error
      checkbox.checked = !checkbox.checked;
      console.error("email-preferences: Request error", error);
    } finally {
      this.setLoadingState(false, item);
    }
  }

  /**
   * Set loading state on all checkboxes
   */
  private setLoadingState(loading: boolean, activeItem?: HTMLElement) {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.disabled = loading;
    });

    // Add visual loading indicator to the active item
    if (activeItem) {
      if (loading) {
        activeItem.classList.add("opacity-60", "pointer-events-none");
      } else {
        activeItem.classList.remove("opacity-60", "pointer-events-none");
      }
    }
  }

  // Prefer a token minted by the controller's own (uncached, session-bound)
  // fragment over the page <meta>: on the CDN-cached arena homepage the page
  // meta is session-less and would 422 the PATCH. Falls back to the meta on
  // private pages (e.g. /app/settings/email_preferences) that pass no value.
  private get csrfToken(): string {
    if (this.csrfTokenValue) return this.csrfTokenValue;
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta?.getAttribute("content") || "";
  }
}

// Make Turbo available for TypeScript
declare const Turbo: {
  renderStreamMessage(html: string): void;
};
